import {TriangleDownIcon} from '@primer/octicons-react'
import {Text, type TextProps} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'

import {omit} from '../../../../../utils/omit'
import {MemexColumnDataType} from '../../../../api/columns/contracts/memex-column'
import {ViewerPrivileges} from '../../../../helpers/viewer-privileges'
import {Resources} from '../../../../strings'
import {SanitizedHtml} from '../../../dom/sanitized-html'
import styles from './text-value-with-fallback.module.css'

type SidebarTextValueWithFallbackProps = ({text?: string} | {dangerousHtml?: string}) & FallbackTextProps & TextProps

export const TextValueWithFallback = ({columnName, columnType, ...props}: SidebarTextValueWithFallbackProps) => {
  if ('dangerousHtml' in props && props.dangerousHtml) {
    return <SanitizedHtml {...omit(props, ['dangerousHtml'])}>{props.dangerousHtml}</SanitizedHtml>
  }
  if ('text' in props && props.text) {
    return <Text {...omit(props, ['text'])}>{props.text}</Text>
  }
  return <FallbackText columnName={columnName} columnType={columnType} {...props} />
}

type FallbackTextProps = {
  columnName: string
  columnType?: MemexColumnDataType
} & TextProps

const FallbackText = ({columnName, columnType, className, ...props}: FallbackTextProps) => {
  const {hasWritePermissions} = ViewerPrivileges()

  if (!hasWritePermissions) {
    return (
      <Text className={clsx(styles.emptyFieldText, className)} {...props}>
        {Resources.noPermissionEmptyColumnValue(columnName)}
      </Text>
    )
  }
  let emptyTextValue
  switch (columnType) {
    case MemexColumnDataType.Number: {
      emptyTextValue = Resources.emptyColumnNameValue.number
      break
    }
    case MemexColumnDataType.Date: {
      emptyTextValue = Resources.emptyColumnNameValue.date
      break
    }
    case MemexColumnDataType.Text: {
      emptyTextValue = Resources.emptyColumnNameValue.text
      break
    }
    case MemexColumnDataType.SingleSelect: {
      emptyTextValue = Resources.emptyColumnNameValue.singleSelect
      break
    }
    case MemexColumnDataType.Iteration: {
      emptyTextValue = Resources.emptyColumnNameValue.iteration
      break
    }
    default: {
      emptyTextValue = Resources.addColumnNameValue(columnName)
      break
    }
  }
  return (
    <div>
      <Text className={clsx(styles.emptyFieldText, className)} {...props}>
        {emptyTextValue}
      </Text>
      {(columnType === MemexColumnDataType.Iteration || columnType === MemexColumnDataType.SingleSelect) && (
        <Octicon icon={TriangleDownIcon} size="small" className={styles.dropdownIndicator} />
      )}
    </div>
  )
}
